// src/components/PopularChallenges.js 
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ChallengeLiveModal from "./ChallengeLiveModal";
import pb from "../lib/pb";
import Peer from "peerjs";
import VideoPlayer from "../components/VideoPlayer";

export default function PopularChallenges() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [openLive, setOpenLive] = useState(false);

  const liveVideoRef = useRef(null);
  const loadingRef = useRef(false);
  const peerRef = useRef(null);

  const navigate = useNavigate();

  /* 📥 Load popular + live sessions */
  const load = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const list = await pb.collection("popular_challenges").getList(1, 5, {
        sort: "-acceptedCount",
        expand: "author,challengeRef,analyticsRef",
      });

      const live = await pb.collection("live_sessions").getList(1, 50, {
        filter: "isLive=true",
        expand: "host",
      });

      const liveMap = {};
      live.items.forEach((l) => {
        if (l.expand?.host?.id) {
          liveMap[l.expand.host.id] = l;
        }
      });

      setItems(
        (list.items || []).map((i) => ({
          ...i,
          live: liveMap[i.expand?.author?.id] || null,
        }))
      );
    } catch (err) {
      if (
        err?.isAbort ||
        err?.name === "AbortError" ||
        err?.status === 0
      ) {
        return;
      }
      console.error("Popular load error:", err);
    } finally {
      loadingRef.current = false;
    }
  };

  /* 🔁 Initial load + realtime sync */
  useEffect(() => {
    load();
    const unsub = pb.collection("live_sessions").subscribe("*", load);

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, []);

  /* 🔴 Viewer: attach to live stream */
  useEffect(() => {
    const current = items[idx];
    if (!current?.live || !liveVideoRef.current) return;

    const authorPeerId = current.expand?.author?.peerId;
    if (!authorPeerId) return;

    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }

    const peer = new Peer();
    peerRef.current = peer;

    peer.on("open", () => {
      const call = peer.call(authorPeerId);
      if (!call) return;

      call.on("stream", (remoteStream) => {
        const video = liveVideoRef.current;
        if (!video) return;

        video.srcObject = remoteStream;
        video.onloadedmetadata = () => {
          video.play().catch(() => {});
        };
      });

      call.on("error", (err) => {
        console.error("Live call error:", err);
      });
    });

    peer.on("error", (err) => {
      console.error("Peer error:", err);
    });

    return () => {
      peer.destroy();
      peerRef.current = null;
    };
  }, [idx, items]);

  if (!items.length) return null;

  const current = items[idx];
  const data = current.expand?.challengeRef;
  const analytics = current.expand?.analyticsRef;

  const resolvedChallengeId =
  current.expand?.challengeRef?.id || null;



  const mediaURL =
    current.featuredMedia
      ? pb.files.getURL(current, current.featuredMedia)
      : data?.media?.length
      ? pb.files.getURL(data, data.media[0])
      : null;

  return (
    <div className="popular-wrapper">
      {current.live ? (
        <video
          ref={liveVideoRef}
          autoPlay
          playsInline
          muted
          className="popular-img"
        />
      ) : (
        <img className="popular-img" src={mediaURL} alt="" />
      )}

      <h3>{data?.title}</h3>
      <p>{data?.description}</p>

      <div className="popular-footer">
        <div className="left">
          <img
            className="avatar"
            src={
              current.expand?.author?.avatar
                ? pb.files.getURL(
                    current.expand.author,
                    current.expand.author.avatar
                  )
                : ""
            }
            alt=""
          />
          <div>
            <div>{current.expand?.author?.username}</div>
            <small>
              👀 {analytics?.views || 0} • ✅{" "}
              {analytics?.acceptedCount || 0}
              {current.live && " • 🔴 LIVE"}
            </small>
          </div>
        </div>

        <div className="right">
          <button onClick={() => setIdx((idx - 1 + items.length) % items.length)}>
            ◀
          </button>
          <button onClick={() => setIdx((idx + 1) % items.length)}>
            ▶
          </button>
          <button onClick={() => navigate("/explore")}>
            🔍 Explore
          </button>
          <button
            disabled={!resolvedChallengeId}
            onClick={() => {
              if (!resolvedChallengeId) {
                alert("Challenge not ready");
                return;
              }
              setOpenLive(true);
            }}
          >
            🔴 Challenge Live
          </button>
        </div>
      </div>

      {openLive && (
        <ChallengeLiveModal
          open={openLive}
          onClose={() => setOpenLive(false)}
          challengeId={resolvedChallengeId}
        />
      )}
    </div>
  );
}